import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Meta Pixel for Agendik — ID 28877470805219738.
 *
 * Spec (Feb 2026):
 *  - Loads AND fires only on PUBLIC pages. Patient flows, doctor dashboard
 *    and admin panel never load the pixel and never emit any event.
 *  - Advanced matching OFF. We never pass names, emails, phone numbers, or
 *    appointment/health data to Meta.
 *  - PageView on every public route change (after the first load).
 *  - Lead and CompleteRegistration are emitted via the exported helpers from
 *    the doctor signup flow.
 *
 * SPA caveat: in a React SPA the <head> is global, so we can't put the pixel
 * literally inside <head>. Instead this component is mounted once in App and
 * injects the pixel script dynamically the FIRST time the user lands on (or
 * navigates to) a public route — never on patient / dashboard / admin / API /
 * reset / verify routes.
 */

const PIXEL_ID = "28877470805219738";

const PUBLIC_PREFIXES = [
  "/para-medicos",
  "/sobre-nosotros",
  "/legal/",
  "/doctor/login",
  "/doctor/registro",
];
const PUBLIC_EXACT = new Set(["/"]);
const PRIVATE_PREFIXES = [
  "/paciente/", "/doctor/panel", "/admin",
  "/socio/", "/cita/", "/payment/", "/resena",
  "/confirmar-cita", "/embed/", "/buscar", "/medicos/",
  "/doctor/reset",
];

function isPublicPath(pathname) {
  if (PRIVATE_PREFIXES.some(p => pathname.startsWith(p))) return false;
  if (PUBLIC_EXACT.has(pathname)) return true;
  return PUBLIC_PREFIXES.some(p => pathname === p || pathname.startsWith(p));
}

function loadPixelScript() {
  if (typeof window === "undefined" || window.__agendik_pixel_loaded) return;
  // Standard Facebook Pixel bootstrap. Advanced matching stays OFF (no second
  // arg to fbq('init', ...)).
  /* eslint-disable */
  (function(f,b,e,v,n,t,s){
    if(f.fbq)return;
    n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments) : n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
    t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', PIXEL_ID);
  /* eslint-enable */
  window.__agendik_pixel_loaded = true;
  // <noscript> fallback — appended once per session.
  const img = document.createElement("img");
  img.height = 1; img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
  document.body.appendChild(img);
}

export default function MetaPixel() {
  const loc = useLocation();
  const lastPath = useRef("");
  useEffect(() => {
    if (!isPublicPath(loc.pathname)) return;
    loadPixelScript();
    if (loc.pathname === lastPath.current) return;
    lastPath.current = loc.pathname;
    try { window.fbq && window.fbq("track", "PageView"); } catch { /* ignore */ }
  }, [loc.pathname]);
  return null;
}

/** Fire Lead when the doctor clicks the signup CTA on a public page. */
export function trackLead() {
  if (!isPublicPath(window.location.pathname)) return;
  if (!window.__agendik_pixel_loaded) loadPixelScript();
  try { window.fbq && window.fbq("track", "Lead"); } catch { /* ignore */ }
}

/** Fire CompleteRegistration right after a successful /doctor/register call. */
export function trackCompleteRegistration() {
  if (!window.__agendik_pixel_loaded) loadPixelScript();
  try { window.fbq && window.fbq("track", "CompleteRegistration"); } catch { /* ignore */ }
}
